/**
 * CW-SF-D1 TKT-5 — Top Accounts by Open Pipeline widget.
 *
 * Sum open Amount per AccountId, descending. Each row click deep-
 * links to the M3 TKT-7 dossier mode (?provider=salesforce&parent=
 * sf-primary-Account-{id}) inside the Connected Search experience.
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface AccountBucket {
  external_file_id: string;
  account_name: string | null;
  account_url: string | null;
  total: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  accounts: AccountBucket[];
  amount_denied: boolean;
  total_visible: number;
}

const REFRESH_S = 5 * 60;

export function SfTopAccountsByPipelineWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-top-accounts', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--top-accounts">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.accounts?.length ?? 0) > 0}
        emptyMessage={
          data?.amount_denied
            ? "Your Salesforce profile doesn't have access to Opportunity Amount."
            : 'No open team pipeline by account.'
        }
      >
        <ul className="sf-widget__list">
          {data?.accounts.map((a) => {
            const dossierUrl = `/modules/cw/files?provider=salesforce&parent=${encodeURIComponent(a.external_file_id)}`;
            return (
              <li key={a.external_file_id} className="sf-widget__row">
                <RouterLink to={dossierUrl} className="sf-widget__row-name">
                  {a.account_name ?? 'Account (no access)'}
                </RouterLink>
                <div className="sf-widget__row-chips">
                  <span className="sf-widget__chip sf-widget__chip--amount">
                    {formatCurrency(a.total)}
                  </span>
                  <span className="sf-widget__chip">{a.count} opps</span>
                  {a.account_url && (
                    <a
                      href={a.account_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sf-widget__chip-icon"
                      aria-label="Open in Salesforce"
                    >
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {data && data.total_visible > 0 && (
          <div className="sf-widget__footer-meta">
            Visible to you: {formatCurrency(data.total_visible)}
          </div>
        )}
      </SfWidgetShell>
    </div>
  );
}
